import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

/**
 * Shared formatting helpers for the admin SPA.
 *
 * All money is BYN (the only currency the marketplace transacts in v1),
 * all timestamps render in Minsk time (UTC+3) regardless of the admin's
 * browser tz so ops reasons about a single clock (PLAN §3.9 / §1.5).
 *
 * dayjs plugins are registered here once (module side-effect). `dayjs`
 * is tree-shakeable and ~7kb min+gz vs. luxon/moment — chosen as the
 * MUI X DatePickers adapter dep anyway, so reused here for free.
 *
 * `dayjs.extend` / `dayjs.locale` are the documented default-export
 * methods; the lint rule mistakes them for the package's same-named
 * named exports.
 */
/* eslint-disable import/no-named-as-default-member */
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.locale('ru');
/* eslint-enable import/no-named-as-default-member */

/** Business timezone — all admin timestamps render in Minsk (UTC+3). */
export const ADMIN_TIMEZONE = 'Europe/Minsk';

const BYN_FORMATTER = new Intl.NumberFormat('ru-BY', {
  style: 'currency',
  currency: 'BYN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format a money amount as BYN, e.g. `formatCurrency(149.5)` → "149,50 Br".
 * `amount` is a major-unit number (rubles, not kopecks). Non-finite input
 * yields an em-dash so a bad value never renders "NaN" in a grid cell.
 */
export const formatCurrency = (amount: number, currency = 'BYN'): string => {
  if (!Number.isFinite(amount)) return '—';
  if (currency === 'BYN') return BYN_FORMATTER.format(amount);
  return new Intl.NumberFormat('ru-BY', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format an ISO-8601 timestamp as Minsk-local date+time, e.g.
 * "26.05.2026, 14:32". Invalid / empty input yields an em-dash.
 */
export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = dayjs(iso);
  if (!d.isValid()) return '—';
  return d.tz(ADMIN_TIMEZONE).format('DD.MM.YYYY, HH:mm');
};

/**
 * Format an ISO-8601 timestamp as a Minsk-local date only, e.g.
 * "26.05.2026". Invalid / empty input yields an em-dash.
 */
export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = dayjs(iso);
  if (!d.isValid()) return '—';
  return d.tz(ADMIN_TIMEZONE).format('DD.MM.YYYY');
};

/**
 * Format a process-uptime duration (seconds) as a compact Russian
 * "Nд Nч Nм" string, e.g. `formatUptime(275400)` → "3д 4ч 30м". Shows at
 * most the two most-significant non-zero units; sub-minute uptimes read
 * "<1м". Negative / non-finite input yields an em-dash.
 *
 * Used by the dashboard system-health tile — the `/health` payload
 * carries `uptime` in seconds (`process.uptime()`), NOT a percentage, so
 * we render an honest duration rather than inventing an uptime %.
 */
export const formatUptime = (seconds: number): string => formatDurationShort(seconds * 1000);

/**
 * Format a bare duration (milliseconds) as a compact "Nд Nч Nм" string, e.g.
 * `formatDurationShort(11_520_000)` → "3ч 12м". At most the two
 * most-significant non-zero units; sub-minute reads "<1м"; negative /
 * non-finite input yields an em-dash.
 *
 * The bare form (AUT-328) exists because a hold countdown reads «осталось
 * 3 ч 12 мин», not «через 3 часа»: `formatRelative` rounds to one unit, and
 * "через 3 часа" on a window that closes in 3h12m loses exactly the twelve
 * minutes an operator is deciding on. The «осталось …» framing itself is an
 * i18n key — this returns only the number, so rule #11 stays intact.
 */
export const formatDurationShort = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return '<1м';
  const totalMinutes = Math.floor(seconds / 60);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}д`);
  if (hours > 0) parts.push(`${hours}ч`);
  if (minutes > 0) parts.push(`${minutes}м`);
  return parts.slice(0, 2).join(' ');
};

/**
 * The Minsk-local hour-of-day (0..23) of an ISO-8601 timestamp. Used by
 * the dashboard orders-by-hour histogram to highlight the "current" bucket
 * off the server-supplied `computedAt` (Minsk buckets, so the highlight
 * must be a Minsk hour too). Invalid / empty input yields `null`.
 */
export const hourInMinsk = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const d = dayjs(iso);
  if (!d.isValid()) return null;
  return d.tz(ADMIN_TIMEZONE).hour();
};

/**
 * Format an ISO-8601 timestamp relative to now, e.g. "3 минуты назад".
 * Localised via dayjs `ru` locale. Invalid / empty input yields an
 * em-dash. `now` is injectable for deterministic tests.
 */
export const formatRelative = (
  iso: string | null | undefined,
  now: dayjs.ConfigType = undefined,
): string => {
  if (!iso) return '—';
  const d = dayjs(iso);
  if (!d.isValid()) return '—';
  return d.from(dayjs(now));
};
